/* eslint-disable no-useless-escape */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useEffect } from "react";
import { Box, Modal, Typography } from "@mui/material";
import Button from "components/atoms/Button";

import { Delete as DeleteIcon, Lock as LockIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import DealerCare from "services/dealerCareApi";
import { SubmitHandler, useForm } from "react-hook-form";
import Input from "components/Input";
import {
  DealerUserRecoveryPasswordRequest,
  DealerUsersProps,
} from "store/modules/DealerUsers/types.store";
import { EMAIL_PATTERN_REGEX } from "utils/formatByText";
import {
  formatCpf,
  maskCpfOnInput,
  stripTaxDocumentMask,
} from "utils/taxDocument";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import { toast } from "utils/toast";
import i18next from "i18next";
import { formatByText } from "utils";
import { handleGetDealerUsersRequest } from "store/modules/DealerUsers/thunk.store";
import { useAppDispatch } from "store/hooks";
import * as Styled from "./styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { validateSurname } from "pages/ManageUsers/helpers";

interface ModalEditUserProps {
  user: DealerUsersProps | null;
  isOpen: boolean;
  onClose: () => void;
  passwordTemporary: (user: DealerUserRecoveryPasswordRequest) => void;
  deleteUser?: () => void;
}

interface EditUserData {
  email: string;
  name: string;
  cpf?: string;
  imageUrl?: string;
  profile: string;
  cellphone: string;
  surname: string;
}

const optionsProfile = [
  { value: "administrator" },
  { value: "deliveryman" },
  { value: "attendant" },
];

const ModalEditUser = ({
  isOpen,
  onClose,
  user,
  passwordTemporary,
  deleteUser,
}: ModalEditUserProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
  } = useForm<EditUserData>();

  const profileSelected = watch("profile");
  const isSelectedDeliveryman = profileSelected === "deliveryman";

  useEffect(() => {
    if (user) {
      setValue("email", user.email);
      setValue("name", user.name);
      setValue("surname", user.surname);
      setValue("cpf", formatCpf(user.cpf));
      setValue(
        "cellphone",
        formatByText.cellPhoneBr(
          `${user.cellphone.areaCode}${user.cellphone.number}`,
        ),
      );
      setValue("profile", user.profile);
    }
  }, [setValue, user]);

  useEffect(() => {
    if (user && profileSelected === "deliveryman") {
      setValue("cpf", formatCpf(user.cpf) ?? "");
    } else {
      setValue("email", user?.email ?? "");
    }
  }, [profileSelected, setValue, user]);

  const handleDealerUserEdit: SubmitHandler<EditUserData> = useCallback(
    async (data) => {
      try {
        dispatch(setIsSpinnerVisible(true));

        await DealerCare.put(
          `/v1/dealers/${user?.dealerId}/users/${user?.id}`,
          {
            cpf:
              data.profile === "deliveryman" && data?.cpf?.length
                ? stripTaxDocumentMask(data.cpf)
                : undefined,
            email: data.profile === "deliveryman" ? undefined : data?.email,
            name: data.name,
            surname: data.surname,
            profile: data.profile,
          },
        );

        await dispatch(
          handleGetDealerUsersRequest({
            dealerId: user?.dealerId as string,
            limit: 20,
            offset: 0,
          }),
        );
        onClose();
      } catch (err) {
        toast(i18next.t("failedEditUserPleaseAgain").toString(), {
          type: "error",
        });
      } finally {
        dispatch(setIsSpinnerVisible(false));
      }
    },
    [dispatch, onClose, user?.dealerId, user?.id],
  );

  const handleSendEmailPasswordTemporay = () => {
    onClose();
    passwordTemporary({
      email: user?.email ?? "",
      cpf: user?.cpf ?? "",
      cellPhone: user?.cellphone ?? {
        number: "",
        areaCode: "",
        countryCode: "",
      },
      scope: user?.profile ?? "",
    });
  };

  const handleDealerUserDelete = useCallback(async () => {
    deleteUser?.();
    onClose();
  }, [deleteUser, onClose]);

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose} closeAfterTransition keepMounted>
      <Styled.Content>
        <Styled.Form onSubmit={handleSubmit(handleDealerUserEdit)}>
          {user && (
            <>
              {" "}
              <Styled.RowPermission>
                <Input
                  name="name"
                  error={errors.name}
                  label={`${t("name")}*`}
                  register={register}
                  fullWidth
                  inputProps={{
                    maxLength: 256,
                  }}
                  registerOptions={{
                    pattern: {
                      value: /^[A-Za-zÀ-ÖØ-öø-ÿ]{2,} ?$/,
                      message: "Nome inválido",
                    },
                    required: t("requiredField"),
                    onBlur: (e) => setValue("name", e.target.value?.trim()),
                  }}
                  sx={{ mb: 2 }}
                />{" "}
                <Input
                  name="surname"
                  error={errors.surname}
                  label={`${t("surname")}*`}
                  register={register}
                  fullWidth
                  inputProps={{
                    maxLength: 256,
                  }}
                  registerOptions={{
                    required: t("requiredField"),
                    validate: (value: string) => validateSurname(value),
                  }}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Styled.RowPermission>
              <Styled.RowPermission>
                <Input
                  select
                  disabled
                  name="profile"
                  label={`${t("permission")}*`}
                  error={errors.profile}
                  register={register}
                  selectOptions={optionsProfile.map((option) => ({
                    value: option.value,
                    name: t(option.value),
                  }))}
                  sx={{ ml: 1 }}
                  fullWidth
                  registerOptions={{
                    required: t("requiredField"),
                  }}
                  InputLabelProps={{ shrink: true }}
                  defaultValue={user.profile}
                />

                {isSelectedDeliveryman ? (
                  <Input
                    name="cpf"
                    error={errors.cpf}
                    label="CPF*"
                    register={register}
                    fullWidth
                    type="text"
                    inputProps={{
                      maxLength: 14,
                    }}
                    registerOptions={{
                      required: t("requiredField"),
                      onBlur: (e) => setValue("cpf", e.target.value?.trim()),
                      onChange: (e) => {
                        return setValue("cpf", maskCpfOnInput(e.target.value));
                      },
                    }}
                    sx={{ mb: 1 }}
                  />
                ) : (
                  <Input
                    name="email"
                    error={errors.email}
                    label={`${t("email")}*`}
                    register={register}
                    fullWidth
                    required
                    registerOptions={{
                      required: t("requiredField"),
                      pattern: {
                        value: EMAIL_PATTERN_REGEX,
                        message: "Endereço de e-mail inválido",
                      },
                      onBlur: (e) => setValue("email", e.target.value?.trim()),
                    }}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Styled.RowPermission>
              <Button
                color="inherit"
                variant="outlined"
                disabled={!user?.cpf && profileSelected === "deliveryman"}
                endIcon={<LockIcon />}
                sx={{
                  height: "36px !important",
                  mb: 0,
                  ml: 0,
                }}
                onClick={handleSendEmailPasswordTemporay}
              >
                {t("generateTemporaryPassword").toUpperCase()}
              </Button>
            </>
          )}
          {isSelectedDeliveryman && (
            <Styled.Instructions>
              <ErrorOutlineIcon />
              <Box display="flex" alignItems="center" ml={1}>
                <Typography variant="body2" ml={0.5} color="#444142">
                  <Typography
                    variant="body1"
                    sx={{ display: "inline" }}
                    fontWeight="medium"
                    mr={0.5}
                  >
                    {`${t("important")}:`}
                  </Typography>
                  {t("instructionsUserText")}
                </Typography>
              </Box>
            </Styled.Instructions>
          )}
          <Styled.ActionsContainer>
            <Button
              color="warning"
              endIcon={<DeleteIcon />}
              variant="contained"
              onClick={handleDealerUserDelete}
            >
              {t("delete").toUpperCase()}
            </Button>
            <Styled.ContainerButton>
              <Button color="inherit" variant="outlined" onClick={onClose}>
                {t("close").toUpperCase()}
              </Button>
              <Button color="error" variant="contained" type="submit">
                {t("save").toUpperCase()}
              </Button>
            </Styled.ContainerButton>
          </Styled.ActionsContainer>
        </Styled.Form>
      </Styled.Content>
    </Modal>
  );
};

export default ModalEditUser;
